import { useEffect, useState } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ResourceCard from "./ResourceCard";
import { CircularProgress, Divider } from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useGetResourcesHeadersQuery, useSortResourcesMutation } from "../../../../services/resourcesApi";
import { useNotification } from '../../../../context/NotificationContext';

export default function ResourcesList({
  resources,
  onEditHandler,
  onDeleteHandler,
}) {

  const { showNotification } = useNotification();
  
  const [stateResources, setStateResources] = useState(resources?.map((r) => ({ ...r })).sort((a, b) => a.orderNumber - b.orderNumber));
  const [saveSort, { isLoading }] = useSortResourcesMutation();
  const { data: resourcesHeaders, error: resourcesHeaderError, isLoading: resourcesHeadersIsLoading } = useGetResourcesHeadersQuery(); 

  useEffect(() => {
    if (resources) {
      // create a mutable copy of the resources array

      const writableResources = resources
        .map((r) => ({ ...r }))
        .sort((a, b) => a.orderNumber - b.orderNumber);
      setStateResources(writableResources);
    }
  }, [resources]);

  const onDragEnd = (result) => {
    // A: if dropped outside the list, do nothing so the list remains the same
    if (!result.destination) return;

    // A: reorder the resources array
    //make a copy of the resources array
    const items = Array.from(stateResources);
    // extract the resource that was dragged and delete it from the array
    const [reorderedItem] = items.splice(result.source.index, 1);
    // insert the resource at the new index
    items.splice(result.destination.index, 0, reorderedItem);
    // assign the new orderNumber to each resource by the index in the array
    items.forEach((resource, index) => {
      resource.orderNumber = index;
    });
    // sort the array by the orderNumber
    items.sort((a, b) => a.orderNumber - b.orderNumber);

    // save order in DB
    saveSorting(items);
  };

  const saveSorting = async () => {

    const editSortList = stateResources.map((resource) => {
      return {
        id: resource.id,
        orderNumber: resource.orderNumber,
      };
    });

    try {
      let result = await saveSort(editSortList).unwrap();
      const writableResources = result
        .map((r) => ({ ...r }))
        .sort((a, b) => a.orderNumber - b.orderNumber);
      setStateResources(writableResources);
    } catch (error) {
      if (error.status == 401) return; //error managed on baseQueryWithReauth
      showNotification('there was an error saving the resources order', 'error');
      throw error;
    }
  };

  return (
    <div>

      <Accordion
        sx={{
          "& .MuiTypography-root": {
            fontSize: "20px",
          },
          "& .MuiExpansionPanel": {
            root: {
              "&:before": {
                display: "none",
              },
            },
          },
        }}
        elevation={0}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <AccordionSummary
            expandIcon={
              <ExpandMoreIcon
                sx={{ color: "var(--orange)", margin: "0 1rem" }}
              />
            }
            sx={{
              "& .MuiButtonBase-root.MuiAccordionSummary-root": {
                flexGrow: 0,
                justifyContent: "flex-start",
              },
            }}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <h4>Resources</h4>
          </AccordionSummary>
        </div>
        <AccordionDetails>
          {isLoading || resourcesHeadersIsLoading ? (
            <CircularProgress />
          ) : (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="resources">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {(stateResources || []).map((resource, index) => (
                      <Draggable
                        key={resource.id}
                        draggableId={`${resource.id}`}
                        index={index}
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <ResourceCard
                              resource={resource}
                              onEditHandler={onEditHandler}
                              onDeleteHandler={onDeleteHandler}
                              resourcesHeaders={resourcesHeaders}
                            />
                            <Divider sx={{ margin: "1em 0 " }} />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </AccordionDetails>
      </Accordion>
    </div>
  );
}
